import React, { useState, useEffect } from 'react'
import Menu from "../Menu";

const SystemParamList = () => {
  const [systemParams, setSystemParams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingParam, setEditingParam] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', value: '' });

  const token = getCookie("token") || getCookie("authToken");
  const userid = getCookie("userid") || getCookie("userId");

  // Base URL and headers
  const baseURL = 'https://api.dqr-display-tms.eventum.co.in/api/SystemParam';
  const headers = {
    'accept': 'text/plain',
    'UserId': userid,
    'X-Authorization': token
  };

  const transformParams = (apiData) => {
    // apiData expected shape: { data: { key_type: value, ... }, ... }
    const result = [];
    const store = apiData && apiData.data ? apiData.data : {};
    Object.entries(store).forEach(([rawKey, rawValue]) => {
      const lastUnderscore = rawKey.lastIndexOf("_");
      const baseName = lastUnderscore > -1 ? rawKey.slice(0, lastUnderscore) : rawKey;
      const type = lastUnderscore > -1 ? rawKey.slice(lastUnderscore + 1) : "text";
      result.push({ key: rawKey, name: baseName, type, value: rawValue ?? "" });
    });
    return result;
  };

  const groupByType = (params) => {
    const groups = { bool: [], int: [], text: [], longtext: [], combo: [], other: [] };
    for (const p of params) {
      if (groups[p.type]) groups[p.type].push(p);
      else groups.other.push(p);
    }
    return groups;
  };

  // API Call 1: GET /api/SystemParam/Get
  const fetchSystemParams = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/Get`, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setSystemParams(transformParams(data));
    } catch (err) {
      setError('Failed to fetch system parameters: ' + err.message);
      console.error('Error fetching system params:', err);
    } finally {
      setLoading(false);
    }
  };

  // API Call 2: GET /api/SystemParam/ResetScoket
  const resetSocket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/ResetScoket`, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.text();
      alert('Socket reset successfully: ' + result);
    } catch (err) {
      setError('Failed to reset socket: ' + err.message);
      console.error('Error resetting socket:', err);
    } finally {
      setLoading(false);
    }
  };

  // API Call 3: POST /api/SystemParam/Update
  const updateSystemParam = async (paramData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/Update`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify([paramData])
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.text();
      alert('System parameter updated successfully: ' + result);
      
      // Refresh the list after update
      await fetchSystemParams();
    } catch (err) {
      setError('Failed to update system parameter: ' + err.message);
      console.error('Error updating system param:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load system parameters on component mount
  useEffect(() => {
    fetchSystemParams();
  }, []);

  const handleEdit = (param) => {
    setEditingParam(param.key);
    setEditForm({ name: param.name, value: param.value, key: param.key, type: param.type });
  };

  const handleSave = () => {
    if (editForm && editForm.name) {
      // Send base name without type suffix, e.g., delayqueue (not delayqueue_int)
      updateSystemParam({ name: editForm.name, value: String(editForm.value) });
      setEditingParam(null);
      setEditForm({ name: '', value: '' });
    }
  };

  const handleCancel = () => {
    setEditingParam(null);
    setEditForm({ name: '', value: '' });
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">System Parameters</h3>
                    <div className="card-tools">
                      <button 
                        className="btn btn-primary btn-sm mr-2" 
                        onClick={fetchSystemParams}
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Refresh'}
                      </button>
                      <button 
                        className="btn btn-warning btn-sm" 
                        onClick={resetSocket}
                        disabled={loading}
                      >
                        Reset Socket
                      </button>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}
                    
                    {loading && (
                      <div className="text-center">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    )}
                    
                    {!loading && systemParams.length === 0 && !error && (
                      <div className="alert alert-info">
                        No system parameters found.
                      </div>
                    )}
                    
                    {!loading && systemParams.length > 0 && (() => {
                      const groups = groupByType(systemParams);
                      const renderGroup = (title, arr) => (
                        arr.length > 0 && (
                          <>
                            <h5 className="mt-3 mb-2">{title}</h5>
                            <div className="row">
                              {arr.map((param, index) => (
                                <div className="col-md-6 col-lg-4 mb-3" key={`${param.key}-${index}`}>
                                  <div className="card h-100">
                                    <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--info)', color: 'var(--text)' }}>
                                      <div>
                                        <h6 className="mb-0" style={{ color: 'var(--text)' }}>{param.name}</h6>
                                        <small className="text-muted" style={{ color: 'var(--text-muted)' }}>{param.key}</small>
                                      </div>
                                      <span className="badge badge-info text-uppercase" style={{ backgroundColor: 'var(--surface)', color: 'var(--text)' }}>{param.type}</span>
                                    </div>
                                    <div className="card-body">
                                      {editingParam === param.key ? (
                                        param.type === 'bool' ? (
                                          <select
                                            className="form-control"
                                            value={String(editForm.value).toLowerCase() === 'true' ? 'true' : 'false'}
                                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                          >
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                          </select>
                                        ) : param.type === 'longtext' ? (
                                          <textarea
                                            className="form-control"
                                            rows={4}
                                            value={editForm.value}
                                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                          />
                                        ) : param.type === 'int' ? (
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={editForm.value}
                                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                          />
                                        ) : param.type === 'combo' ? (
                                          <select
                                            className="form-control"
                                            value={editForm.value}
                                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                          >
                                            <option value={editForm.value}>{editForm.value}</option>
                                          </select>
                                        ) : (
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.value}
                                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                          />
                                        )
                                      ) : (
                                        <div className="p-2 bg-light border rounded">{String(param.value)}</div>
                                      )}
                                    </div>
                                    <div className="card-footer text-right">
                                      {editingParam === param.key ? (
                                        <>
                                          <button
                                            className="btn btn-success btn-sm mr-2"
                                            onClick={handleSave}
                                            disabled={loading}
                                          >
                                            Save
                                          </button>
                                          <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={handleCancel}
                                          >
                                            Cancel
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          className="btn btn-primary btn-sm"
                                          onClick={() => handleEdit(param)}
                                          disabled={loading}
                                        >
                                          Edit
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )
                      );

                      return (
                        <>
                          {renderGroup('Numbers', groups.int)}
                          {renderGroup('Text', groups.text)}
                          {renderGroup('Long Text', groups.longtext)}
                          {renderGroup('Booleans', groups.bool)}
                          {renderGroup('Combos', groups.combo)}
                          {renderGroup('Other', groups.other)}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SystemParamList;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
